import { CatchBoundary, Link, useNavigate, useRouter, useRouterState } from '@tanstack/react-router'
import { useCallback, useRef, useSyncExternalStore } from 'react'
import { useAuth } from '../auth/auth-context'
import { useThemeSync } from '../../features/profile/use-theme-sync'
import { useOrbit } from '../../features/pick/queries'
import { useGuestOrbit } from '../../features/my-day/guest-orbit'
import { orbitComplete } from '../../features/my-day/orbit-complete'
import { readingTabMarker } from '@lunary/app-core/orbit-guide'
import { BrandWordmark } from '@lunary/ui-web/brand-wordmark'
import { TabBar } from '@lunary/ui-web/tab-bar'
import type { TabItem, TabLinkProps } from '@lunary/ui-web/tab-bar'
import { SwipePager } from '@lunary/ui-web/swipe-pager'
import type { SwipePreview } from '@lunary/ui-web/swipe-pager'
import { RouteError } from '@lunary/ui-web/screen-state'
import { MaintenanceScreen } from '@lunary/ui-web/maintenance-screen'
import { useOutage } from '@lunary/ui-web/outage'
import { useHeaderHeight } from './use-header-height'
import styles from './shell.module.css'

type WebTab = TabItem & { to: '/daily-card' | '/pick' | '/my-day' | '/readings' | '/profile'; hash?: string }

const TABS: readonly WebTab[] = [
  { key: 'today', label: 'Сегодня', icon: 'today', to: '/daily-card' },
  { key: 'pick', label: 'Выбор', icon: 'pick', to: '/pick' },
  { key: 'my-day', label: 'Карта дня', icon: 'spark', to: '/my-day', center: true },
  { key: 'readings', label: 'Разборы', icon: 'orders', to: '/readings' },
  { key: 'profile', label: 'Профиль', icon: 'profile', to: '/profile' },
]

const KEYS = TABS.map((t) => t.key)

const pathOf = (key: string) => TABS.find((t) => t.key === key)?.to

const NARROW = '(max-width: 959px)'

const subscribeNarrow = (onChange: () => void) => {
  const query = window.matchMedia(NARROW)
  query.addEventListener('change', onChange)
  return () => query.removeEventListener('change', onChange)
}

const isNarrow = () => window.matchMedia(NARROW).matches

const renderLink = (item: WebTab, props: TabLinkProps) => <Link to={item.to} hash={item.hash} {...props} />

function useActiveTab(): string | null {
  const { pathname } = useRouterState({ select: (s) => s.location })
  if (pathname === '/my-day') return 'my-day'
  if (pathname === '/' || pathname === '/daily-card') return 'today'
  if (pathname === '/pick') return 'pick'
  if (pathname === '/readings' || pathname.startsWith('/readings/')) return 'readings'
  if (pathname === '/profile' || pathname.startsWith('/profile/') || pathname === '/auth') return 'profile'
  return null
}

function usePageKey(): string | null {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  if (pathname === '/') return 'today'
  return TABS.find((t) => t.to === pathname)?.key ?? null
}

export function Shell({ children }: { children: React.ReactNode }) {
  const { status } = useAuth()
  const { user } = useAuth()
  const guest = status === 'guest'
  const own = useOrbit(status === 'authenticated' && user ? user.id : null)
  const guestOrbit = useGuestOrbit(guest)
  const orbit = guest ? guestOrbit.data : own.data
  const attention = !!orbit && !orbitComplete(orbit, guest)
  const active = useActiveTab()
  const router = useRouter()
  const navigate = useNavigate()
  const narrow = useSyncExternalStore(subscribeNarrow, isNarrow)
  const preview = useRef<SwipePreview | null>(null)
  useThemeSync()
  const headerRef = useHeaderHeight<HTMLDivElement>()
  const down = useOutage()

  const readingMarker = readingTabMarker(guest ? null : orbit?.reading)
  const tabs = TABS.map((t) => (t.center ? { ...t, attention } : t.key === 'readings' ? { ...t, marker: readingMarker } : t))
  const pageKey = usePageKey()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const giftPage = pathname === '/readings/gift' || pathname === '/readings/gifts' || pathname.startsWith('/gift/')
  const chat = pathname.startsWith('/readings/') && !giftPage
  const bare = chat || giftPage || pathname === '/profile/lunars/topup'

  const renderPage = (key: string) => {
    const to = pathOf(key)
    const Page = to ? router.routesByPath[to].options.component : undefined
    if (!Page) return null
    return (
      <CatchBoundary getResetKey={() => key} errorComponent={RouteError}>
        <Page />
      </CatchBoundary>
    )
  }

  const onNavigate = (key: string) => {
    const to = pathOf(key)
    if (to) void navigate({ to, viewTransition: false })
  }

  const onPrepare = useCallback(
    (key: string) => {
      const to = pathOf(key)
      if (to) void router.preloadRoute({ to }).catch(() => undefined)
    },
    [router],
  )

  if (down) return <MaintenanceScreen />

  return (
    <div className={chat ? styles.chatFrame : undefined}>
      <div ref={headerRef} className={styles.top}>
        <header className={styles.header}>
          <Link to="/" className={styles.brand}>
            <BrandWordmark height={30} />
          </Link>
          <TabBar items={tabs} activeKey={active} renderLink={renderLink} inline />
          <span className={styles.spacer} aria-hidden="true" />
        </header>
      </div>
      <main className={`shell ${styles.main} ${chat ? styles.chat : ''}`}>
        <SwipePager keys={KEYS} activeKey={pageKey} enabled={narrow} renderPage={renderPage} onNavigate={onNavigate} onPrepare={onPrepare} preview={preview}>
          {children}
        </SwipePager>
      </main>
      {!bare && <TabBar items={tabs} activeKey={active} renderLink={renderLink} previewRef={preview} />}
    </div>
  )
}
